import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import api from '../services/api';
import {
  Page,
  Hero,
  HeroContent,
  HeroTag,
  HeroHighlights,
  Section,
  SectionTitle,
  SectionText,
  Card,
  Callout,
} from './InfoPageStyles';

const FAAct = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [viewerWidth, setViewerWidth] = useState(900);
  const [isMobileView, setIsMobileView] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({ Document: null, PdfPage: null });

  const isProbablyMobile = () => {
    if (typeof window === 'undefined') return false;
    const narrowScreen = window.matchMedia?.('(max-width: 900px)')?.matches;
    const touchCapable = window.matchMedia?.('(pointer: coarse)')?.matches;
    const ua = window.navigator?.userAgent || '';
    const mobileUa = /android|iphone|ipad|ipod|mobile/i.test(ua);
    return Boolean(mobileUa || (narrowScreen && touchCapable));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const data = await api.faAct.get();
        setContent(data);
      } catch (err) {
        setLoadError(err?.message || 'Failed to load FA Act content.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncMobileState = () => setIsMobileView(isProbablyMobile());
    syncMobileState();
    window.addEventListener('resize', syncMobileState);
    return () => window.removeEventListener('resize', syncMobileState);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isMobileView) {
      setPdfViewer({ Document: null, PdfPage: null });
      return;
    }

    const loadPdfViewer = async () => {
      try {
        const module = await import('react-pdf');
        module.pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${module.pdfjs.version}/build/pdf.worker.min.mjs`;
        if (!cancelled) {
          setPdfViewer({ Document: module.Document, PdfPage: module.Page });
          setPdfError('');
        }
      } catch (err) {
        if (!cancelled) {
          setPdfViewer({ Document: null, PdfPage: null });
          setPdfError('Inline PDF preview is unavailable on this device. Please use Open/Download PDF.');
        }
      }
    };

    loadPdfViewer();

    return () => {
      cancelled = true;
    };
  }, [isMobileView]);

  useEffect(() => {
    const syncWidth = () => {
      const viewport = window.innerWidth || 1200;
      setViewerWidth(Math.max(260, Math.min(900, viewport - 48)));
    };
    syncWidth();
    window.addEventListener('resize', syncWidth);
    return () => window.removeEventListener('resize', syncWidth);
  }, []);

  const onLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages || 0);
    setPageNumber(1);
  };

  const normalizePdfDataUri = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^data:application\/pdf;base64,/i.test(trimmed)) return trimmed;
    if (/^data:[^;]+;base64,/i.test(trimmed)) return '';
    if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed)) {
      return `data:application/pdf;base64,${trimmed.replace(/\s+/g, '')}`;
    }
    return '';
  };

  if (loading) {
    return (
      <Page>
        <Section>
          <SectionText>Loading Food Authority Act...</SectionText>
        </Section>
      </Page>
    );
  }

  if (loadError) {
    return (
      <Page>
        <Section>
          <SectionTitle>Food Authority Act</SectionTitle>
          <SectionText>{loadError}</SectionText>
        </Section>
      </Page>
    );
  }

  const highlights = Array.isArray(content?.highlights) ? content.highlights.filter(Boolean) : [];
  const pdfDataUri = normalizePdfDataUri(content?.pdfFile);
  const PdfDocument = pdfViewer.Document;
  const PdfPageComponent = pdfViewer.PdfPage;
  const canRenderInlinePdf = Boolean(pdfViewer.Document && pdfViewer.PdfPage && !isMobileView);
  const canGoPrev = pageNumber > 1;
  const canGoNext = numPages > 0 && pageNumber < numPages;

  return (
    <Page>
      <Hero>
        <HeroContent>
          <HeroTag>{content?.headerTag || 'Guidelines'}</HeroTag>
          <h1>{content?.title || 'Food Authority Act'}</h1>
          <p>{content?.brief || 'The official Food Authority Act document and summary will appear here.'}</p>
          {highlights.length > 0 && (
            <HeroHighlights>
              {highlights.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </HeroHighlights>
          )}
        </HeroContent>
      </Hero>

      <Section>
        <SectionTitle>View Full Act (PDF)</SectionTitle>
        {!pdfDataUri ? (
          <Card>
            <p>No FA Act PDF has been uploaded yet.</p>
          </Card>
        ) : (
          <Card>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14, alignItems: 'center' }}>
              <a
                href={pdfDataUri}
                download={content.pdfFileName || 'food-authority-act.pdf'}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  background: '#15803d',
                  color: '#fff',
                  padding: '10px 14px',
                  borderRadius: 8,
                  fontWeight: 600
                }}
              >
                Open / Download PDF
              </a>
              {canRenderInlinePdf ? (
                <>
                  <button
                    type="button"
                    onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                    disabled={!canGoPrev}
                    style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                    disabled={!canGoNext}
                    style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                  >
                    Next
                  </button>
                  <span style={{ color: '#374151', fontWeight: 500 }}>
                    Page {numPages ? pageNumber : 0} of {numPages || 0}
                  </span>
                </>
              ) : null}
            </div>
            {pdfError ? (
              <div style={{ color: '#b91c1c', marginTop: 6 }}>{pdfError}</div>
            ) : null}
            {isMobileView ? (
              <div style={{ color: '#374151' }}>Inline PDF preview is disabled on mobile for stability. Use Open / Download PDF.</div>
            ) : null}
            {canRenderInlinePdf ? (
              <div style={{ overflowX: 'auto', background: '#f8fafc', borderRadius: 10, padding: 12 }}>
                <PdfDocument
                  file={pdfDataUri}
                  onLoadSuccess={onLoadSuccess}
                  onLoadError={() => setPdfError('Could not render PDF preview on this device. Use Open / Download PDF.')}
                >
                  <PdfPageComponent pageNumber={pageNumber} width={viewerWidth} />
                </PdfDocument>
              </div>
            ) : null}
          </Card>
        )}
      </Section>

      <Section>
        <Callout>
          <strong>Need guidance on the FAL Act?</strong>
          <span>
            Contact the Authority for implementation support or visit the business registry for licensing steps.
          </span>
          <div>
            <Link to="/contact">Contact FAL</Link> | <Link to="/business-registry">Business registry</Link>
          </div>
        </Callout>
      </Section>
    </Page>
  );
};

export default FAAct;
